import {
  Flex as ChakraFlex,
  Tooltip as ChakraTooltip,
  Icon as ChakraIcon,
  Text as ChakraText,
  CloseButton as ChakraCloseButton,
  Switch as ChakraSwitch,
  Center as ChakraCenter,
  Spinner as ChakraSpinner,
  Button as ChakraButton,
  Box as ChakraBox,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react';
import Drawer from 'react-modern-drawer';
import {
  lazy,
  Suspense,
  useState,
  useEffect,
  useReducer,
  useRef,
  type FC,
} from 'react';

// Chakra v2's deeply-typed components blow up TS2590 in this file; cast each
// to a permissive `FC<Record<string, unknown>>` at the import boundary so JSX
// usage stays compact and the Chakra prop union never gets evaluated. Trade:
// prop typos go uncaught for these tags — acceptable since this file is
// established UI rendered untouched from the JS version.
type AnyProps = Record<string, unknown>;
const Flex = ChakraFlex as FC<AnyProps>;
const Tooltip = ChakraTooltip as FC<AnyProps>;
const Icon = ChakraIcon as FC<AnyProps>;
const Text = ChakraText as FC<AnyProps>;
const CloseButton = ChakraCloseButton as FC<AnyProps>;
const Switch = ChakraSwitch as FC<AnyProps>;
const Center = ChakraCenter as FC<AnyProps>;
const Button = ChakraButton as FC<AnyProps>;
const Box = ChakraBox as FC<AnyProps>;
const Spinner = ChakraSpinner as FC<AnyProps>;
import { useHistory } from 'react-router-dom';
import NotificationLine from '@/assets/icons/nav/NotificationLine';
import NotificationSolid from '@/assets/icons/nav/NotificationFilled';
import { useGetNotificationsQuery } from '@/store/query/notification';
import { useDeferredUserDirectory } from './useDeferredUserDirectory';
import { useDispatch, useSelector } from 'react-redux';
import { useNotificationWorker } from './use-notification-worker';

// The list renders every notification type (offers, scorecards, exports…) and
// the side nav that hosts this panel is on every page, so the list is fetched
// when the panel first opens instead of shipping in the entry bundle.
// Its declared interface also omits some props (e.g. `isMarkAllReadOpen`) that
// the runtime component genuinely accepts; widen it so this file can pass the
// same payload it always has.
const NotificationList = lazy(() =>
  import('./list').then((m) => ({
    default: m.NotificationList as FC<AnyProps>,
  }))
);
const LazyOfferApprovalView = lazy(() =>
  import('@/components/ats/OfferInitiation/OfferApprovalView').then((m) => ({
    default: m.OfferApprovalView as unknown as FC<AnyProps>,
  }))
);
// Own boundary, so opening an offer does not swap the whole list for a spinner.
const OfferApprovalView: FC<AnyProps> = (props) => (
  <Suspense fallback={null}>
    <LazyOfferApprovalView {...props} />
  </Suspense>
);
import { clearNotification } from '@/store/notification';
import { selectLiveNotifications } from '@/store/notification/selectors';
// Lazy for the same reason as the list above: this dialog only mounts once the
// user opens "mark all as read", but a static import kept it — and the
// `@/components/ats` barrel it pulls — on the app-shell path.
const MarkAllRead = lazy(() =>
  import('./list/MarkAllRead').then((m) => ({ default: m.MarkAllRead }))
);
import { useMixpanel } from '@/hooks/use-mixpanel';
import { getModifiedNotifications } from '@/utils/getModifiednotifications';

import { NavButton } from '@/layout/side-nav/components/NavButton';
import { useNudges } from '@/hooks';
import { useKFlags } from '@/hooks/use-k-flags';
import { Bell01, CheckCircle, Clock, Settings01 } from '@kula-ai/icons-core';

// Legacy mixpanel call sites in this file pass `{ module, data }` instead of
// the hook's documented `{ event, prop }` shape. Cast so the existing payload
// shape is accepted without rewriting every call site.
type LegacyTrack = (_payload: Record<string, unknown>) => void;

interface NotificationProps {
  showUnread?: boolean;
  onClose?: () => void;
  onToggleUnRead?: () => void;
  refetch?: () => void;
  isLoading?: boolean;
  notifications?: unknown[];
  setMarkAll?: (_value: boolean) => void;
  setPagination?: () => void;
  topNavHeight?: string;
  onNavigateToRecentActivities?: () => void;
  onNavigateToSettings?: () => void;
}

const Notification = ({
  showUnread = true,
  onClose = () => {},
  onToggleUnRead = () => {},
  refetch = () => {},
  isLoading = false,
  notifications = [],
  setMarkAll = () => {},
  setPagination = () => {},
  topNavHeight = '0px',
  onNavigateToRecentActivities = () => {},
  onNavigateToSettings,
}: NotificationProps) => {
  const { track } = useMixpanel() as unknown as { track: LegacyTrack };
  const { isOpen, onOpen, onClose: onCloseMarkRead } = useDisclosure();
  const dispatch = useDispatch();
  const liveNotifications = useSelector(selectLiveNotifications);
  const onClickLiveNotification = () => {
    refetch();
    dispatch(clearNotification());
  };
  const { ats_enabled } = useKFlags();

  const handleRecentActivitiesClick = () => {
    onNavigateToRecentActivities();
    track({
      module: 'in-app-notification',
      data: {
        'user-event': 'click',
        'user-action': 'navigate-recent-activities',
      },
    });
  };

  const handleSettingsClick = () => {
    if (!onNavigateToSettings) return;
    onNavigateToSettings();
    track({
      module: 'in-app-notification',
      data: {
        'user-event': 'click',
        'user-action': 'navigate-notifications-center-settings',
      },
    });
  };

  return (
    <Flex
      py="20px"
      flexDir="column"
      h={`calc(100% - ${topNavHeight})`}
      data-test-id="in-app-notification-container"
    >
      <Flex alignItems="center" px="20px" mb="16px">
        <Flex alignItems="center" gap="8px" flex="1">
          <Text color={'primary-900'} fontSize="20px" fontWeight="semiBold">
            Notifications
          </Text>
          {ats_enabled && (
            <Tooltip label="Notification settings" placement="bottom" hasArrow>
              <Flex
                onClick={handleSettingsClick}
                cursor="pointer"
                w="28px"
                h="28px"
                alignItems="center"
                justifyContent="center"
                borderRadius="6px"
                border="1px solid"
                borderColor="border-color"
                bg="base-white"
                _hover={{ bg: 'secondary-25' }}
                data-test-id="in-app-notification-settings"
              >
                <Icon as={Settings01} w="16px" h="16px" color="secondary-900" />
              </Flex>
            </Tooltip>
          )}
        </Flex>
        <Flex alignItems="center" px="12px">
          <Switch
            className={
              ats_enabled
                ? `ats-switch-toogle ${showUnread ? 'active' : ''}`
                : ''
            }
            data-test-id="show-only-unread"
            isChecked={showUnread}
            onChange={() => {
              track({
                module: 'in-app-notification',
                data: {
                  'user-event': 'click',
                  'user-action': 'show-unread',
                },
              });
              onToggleUnRead();
            }}
          />
          <Text ml="8px" fontSize="12px">
            Show only unread
          </Text>
        </Flex>
        <CloseButton onClick={onClose} />
      </Flex>

      <Flex
        px="20px"
        pb="12px"
        alignItems="center"
        justifyContent="space-between"
        borderBottom="1px solid"
        borderColor="border-color"
      >
        <Text color="secondary-700" fontSize="14px" fontWeight="medium">
          Latest
        </Text>

        <Flex alignItems="center" gap="12px">
          {liveNotifications.length > 0 && (
            <Box
              onClick={onClickLiveNotification}
              cursor="pointer"
              fontSize="14px"
              fontWeight="medium"
              py="8px"
              px="16px"
              border="1px solid var(--kula-dark-secondary-100, #E5E5E5)"
              boxShadow="drop-shadows-bottom"
              borderRadius="62px"
              textAlign="center"
            >
              {liveNotifications.length} New notification
            </Box>
          )}
          {/* Recent activities link for ATS users */}
          {ats_enabled && (
            <Button
              variant="tertiary"
              leftIcon={
                <Clock
                  size={16}
                  color="var(--kula-colors-ats-primary)"
                  className="kula-icon"
                />
              }
              onClick={handleRecentActivitiesClick}
            >
              Recent activities
            </Button>
          )}
          {showUnread && (
            <Tooltip label="Mark all as read" placement="bottom" hasArrow>
              <Flex
                data-test-id="in-notification-mark-all-as-read"
                onClick={() => {
                  setMarkAll(true);
                  onOpen();
                  track({
                    module: 'in-app-notification',
                    data: {
                      'user-event': 'click',
                      'user-action': 'mark-all-read',
                      status: 'open',
                    },
                  });
                }}
                cursor="pointer"
                w="36px"
                h="36px"
                alignItems="center"
                justifyContent="center"
                borderRadius="6px"
                border="1px solid"
                borderColor="border-color"
                bg="base-white"
              >
                <CheckCircle
                  size={20}
                  color="var(--kula-colors-secondary-700)"
                  className="kula-icon"
                />
              </Flex>
            </Tooltip>
          )}
        </Flex>
      </Flex>

      {isLoading ? (
        <Center flex="1" flexDir="column">
          <Spinner color="primary-800" />
        </Center>
      ) : (
        <Suspense
          fallback={
            <Center flex="1" flexDir="column">
              <Spinner color="primary-800" />
            </Center>
          }
        >
          <NotificationList
            // NotificationList's type says `() => void` but its internals call
            // setMarkAll(boolean); the runtime contract is the boolean form.
            setMarkAll={setMarkAll as unknown as () => void}
            notifications={getModifiedNotifications(notifications)}
            isMarkAllReadOpen={isOpen}
            onClose={onClose}
            components={{
              OfferApprovalView,
            }}
          />
        </Suspense>
      )}

      {isOpen && (
        <Suspense fallback={null}>
          <MarkAllRead
            onToggleUnRead={onToggleUnRead}
            setPagination={setPagination}
            isOpen={isOpen}
            onClose={() => {
              setMarkAll(false);
              onCloseMarkRead();
            }}
          />
        </Suspense>
      )}
    </Flex>
  );
};

import { notificationReducer, initialState } from './reducer';

interface NewAppNotificationProps {
  isCollapsed?: boolean;
}

export const NewAppNotification = ({
  isCollapsed,
}: NewAppNotificationProps) => {
  const history = useHistory();
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const [showNotification, setNotification] = useState(false);
  const liveNotifications = useSelector(selectLiveNotifications);
  const [markAllOpen, setMarkAll] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { track } = useMixpanel() as unknown as { track: LegacyTrack };
  const { activeNudges } = useNudges();

  const handleNavigateToRecentActivities = () => {
    setNotification(false);
    history.push('/notifications/recent-activities');
  };

  const handleNavigateToSettings = () => {
    setNotification(false);
    history.push('/settings/notifications-center');
  };

  useOutsideClick({
    enabled: !markAllOpen,
    ref,
    // todo: hiding this for now as it is causing issues with the drawer
    // handler: () => setNotification(false),
  });

  const { data, isFetching, refetch } = useGetNotificationsQuery({
    filter: state,
  });

  useEffect(() => {
    if (isFetching) return;
    refetch();
  }, [showNotification]); // eslint-disable-line react-hooks/exhaustive-deps

  useNotificationWorker();

  const onToggleUnRead = () => {
    dispatch({ type: 'toggleUnRead' });
  };

  const paginationCount =
    state?.unread && state.notification_type === 'all'
      ? (data?.pagination?.total_entries_size ?? 0)
      : 0;

  // Warmed at idle rather than on mount — this bell is in the app shell, so the
  // eager fetch ran on every page load for data most pages never read.
  useDeferredUserDirectory();

  return (
    <>
      <Box
        position="relative"
        data-test-id="in-app-notification"
        onClick={() => {
          setNotification(!showNotification);
          track({
            module: 'in-app-notification',
            data: {
              'user-event': 'click',
              'user-action': 'open-notification',
              notificationCount: paginationCount + liveNotifications?.length,
            },
          });
        }}
      >
        <NavButton
          icon={Bell01}
          isCollapsed={isCollapsed}
          title="Notifications"
          count={paginationCount + liveNotifications?.length}
          active={showNotification}
          notched={showNotification}
          navRow
        />
      </Box>

      {showNotification && (
        <Box ref={ref}>
          <Drawer
            open={showNotification}
            direction="left"
            enableOverlay={false}
            style={{
              left: isCollapsed ? '60px' : '241px',
              width: '500px',
              top: activeNudges?.length > 0 ? '83px' : '51px',
              borderRight: '1px solid var(--kula-dark-secondary-100, #E5E5E5)',
              boxShadow: '9px 4px 10px 1px rgba(0, 0, 0,0.06)',
              zIndex: 99,
            }}
          >
            <Notification
              isLoading={isFetching}
              refetch={refetch}
              notifications={data?.notifications}
              showUnread={state.unread}
              onToggleUnRead={onToggleUnRead}
              onClose={() => {
                setNotification(false);
              }}
              setMarkAll={setMarkAll}
              setPagination={() => {}}
              topNavHeight={activeNudges?.length > 0 ? '83px' : '51px'}
              onNavigateToRecentActivities={handleNavigateToRecentActivities}
              onNavigateToSettings={handleNavigateToSettings}
            />
          </Drawer>
        </Box>
      )}
    </>
  );
};

export const AppNotification = () => {
  const history = useHistory();
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const [showNotification, setNotification] = useState(false);
  const liveNotifications = useSelector(selectLiveNotifications);
  const [markAllOpen, setMarkAll] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { track } = useMixpanel() as unknown as { track: LegacyTrack };

  useOutsideClick({
    enabled: !markAllOpen,
    ref,
    handler: () => setNotification(false),
  });

  const { data, isFetching, refetch } = useGetNotificationsQuery({
    filter: state,
  });

  useNotificationWorker();

  const onToggleUnRead = () => {
    dispatch({ type: 'toggleUnRead' });
  };

  const handleNavigateToSettings = () => {
    setNotification(false);
    history.push('/settings/notifications-center');
  };

  const paginationCount =
    state?.unread && state.notification_type === 'all'
      ? (data?.pagination?.total_entries_size ?? 0)
      : 0;

  // Warmed at idle rather than on mount — this bell is in the app shell, so the
  // eager fetch ran on every page load for data most pages never read.
  useDeferredUserDirectory();

  return (
    <>
      <Flex mb="16px" data-test-id="in-app-notification">
        <Tooltip
          label="Notifications"
          arrowSize={8}
          hasArrow
          placement="right"
          borderRadius="4px"
        >
          <Flex
            position="relative"
            onClick={() => {
              setNotification(!showNotification);
              track({
                module: 'in-app-notification',
                data: {
                  'user-event': 'click',
                  'user-action': 'open-notification',
                  notificationCount:
                    paginationCount + liveNotifications?.length,
                },
              });
            }}
          >
            {(paginationCount > 0 || liveNotifications?.length > 0) && (
              <Center
                position="absolute"
                width="22px"
                height="22px"
                bg="#E7350E"
                color="#FFF"
                borderRadius="full"
                right="-3px"
                zIndex="1"
                fontSize="10px"
                top="-1px"
                border="2px solid #010E1B"
                fontWeight="semiBold"
              >
                {paginationCount + liveNotifications?.length}
              </Center>
            )}
            {showNotification ? (
              <Flex
                width="36px"
                height="36px"
                alignItems="center"
                justifyContent="center"
                className="active"
                bg="#2f3336"
                borderRadius="8px"
                cursor="pointer"
              >
                <Icon
                  as={NotificationSolid}
                  width="18px"
                  height="18px"
                  color="white"
                />
              </Flex>
            ) : (
              <Flex
                width="36px"
                height="36px"
                alignItems="center"
                justifyContent="center"
                className="default"
                cursor="pointer"
                opacity="0.6"
              >
                <Icon
                  as={NotificationLine}
                  width="18px"
                  height="18px"
                  color="white"
                />
              </Flex>
            )}
          </Flex>
        </Tooltip>
      </Flex>

      {showNotification && (
        <Box ref={ref}>
          <Drawer
            open={showNotification}
            direction="left"
            enableOverlay={false}
            style={{
              left: '80px',
              width: '1500px',
              borderRight: ' 1px solid var(--kula-dark-secondary-100, #E5E5E5)',
              borderLeft: ' 1px solid var(--kula-dark-secondary-100, #E5E5E5)',
              boxShadow: '9px 4px 10px 1px rgba(0, 0, 0,0.06)',
            }}
          >
            <Notification
              isLoading={isFetching}
              refetch={refetch}
              notifications={data?.notifications}
              showUnread={state.unread}
              onToggleUnRead={onToggleUnRead}
              onClose={() => {
                setNotification(false);
              }}
              setMarkAll={setMarkAll}
              setPagination={() => {}}
              onNavigateToSettings={handleNavigateToSettings}
            />
          </Drawer>
        </Box>
      )}
    </>
  );
};
